import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useGlobal } from 'context/GlobalContext';
import { useTranslation } from 'react-i18next';
import { useToast } from 'context/ToastContext';
import { useLead, defLeadsStatus } from './context/LeadProvider';
import * as apiLead from 'api/apiLeads';
import { useListRefreshEffect } from 'utils/helpers/list/useListRefreshEffect';
import { useListOnPage } from 'utils/helpers/list/useListOnPage';
import { Card, Col, Row } from 'react-bootstrap';
import LeadsTable from './LeadsTable';
import ESpinner from 'components/others/Spinner';
import LeadToolBar from './LeadToolBar';
import OffNewLead from './OffNewLead';
import { OffClientInfo } from 'modules/client';
import { OffTasks } from 'modules/task';
import TitleCounter from 'components/others/TitleCounter';
import type { LeadType } from '@econtractor/types';

const buildLeadsListOrder = (order: string, desc: boolean) =>
    `${order ? `order by ${order} ` : ''} ${desc ? 'DESC' : ''}`;

const Leads = () => {
    const { t } = useTranslation(['Leads', 'common']);
    const { g } = useGlobal();
    const { showToast } = useToast();
    const {
        showSpinner,
        setShowSpinner,
        leadsOrder,
        leadsDesc,
        leadsSatus,
        setLeadsOrder,
        setLeadsDesc,
        setLeadStatus,
        listResetNonce,
        listRefreshNonce,
    } = useLead();

    const [leadsList, setLeadsList] = useState<LeadType[] | null>(null);
    const [page, setPage] = useState<number | null>(null);
    const [showNewLead, setShowNewLead] = useState(false);
    const [clientInfoId, setClientInfoId] = useState<number | null>(null);
    const [tasksLeadId, setTasksLeadId] = useState<number | null>(null);

    const location = useLocation();
    const onPage = useListOnPage();
    const prevListData = useRef<string | null>(null);
    const skipFetchOnStateChangeRef = useRef(false);
    const resetInFlightRef = useRef(false);

    const fetchLeads = async (force = false): Promise<void | null> => {
        if (force) prevListData.current = null;
        const search = `%${g?.search?.[location.pathname] || ''}%`;
        if (!page || showSpinner) return null;

        const listData = {
            search,
            order: buildLeadsListOrder(leadsOrder, leadsDesc),
            page: page < 0 ? 1 : page || 1,
            onPage,
            status: leadsSatus,
        };

        if (prevListData.current === JSON.stringify(listData)) return null;
        prevListData.current = JSON.stringify(listData);

        setShowSpinner(true);
        const resLeads = await apiLead.getList(listData);
        if (!resLeads) {
            setShowSpinner(false);
            showToast(t('common:messError'), 'danger');
            return null;
        }
        setShowSpinner(false);

        g.setPaginator((prev: Record<string, unknown>) => ({
            ...prev,
            [location.pathname]: resLeads.paginator,
        }));

        setLeadsList(resLeads.leads);
        return null;
    };

    const resetLeadsList = useCallback(async () => {
        if (resetInFlightRef.current) return;
        resetInFlightRef.current = true;
        skipFetchOnStateChangeRef.current = true;
        prevListData.current = null;
        setLeadsOrder('');
        setLeadsDesc(false);
        setLeadStatus(defLeadsStatus);
        g.setPaginatorPage(location.pathname, 1);
        setPage(1);

        const search = `%${g?.search?.[location.pathname] || ''}%`;
        const listData = {
            search,
            order: buildLeadsListOrder('', false),
            page: 1,
            onPage,
            status: defLeadsStatus,
        };

        setShowSpinner(true);
        const resLeads = await apiLead.getList(listData);
        setShowSpinner(false);

        if (!resLeads) {
            skipFetchOnStateChangeRef.current = false;
            resetInFlightRef.current = false;
            showToast(t('common:messError'), 'danger');
            return;
        }

        prevListData.current = JSON.stringify(listData);

        g.setPaginator((prev: Record<string, unknown>) => ({
            ...prev,
            [location.pathname]: resLeads.paginator,
        }));

        setLeadsList(resLeads.leads);
        // Після ефектів від setState скидання — щоб не було другого fetch
        setTimeout(() => {
            skipFetchOnStateChangeRef.current = false;
            resetInFlightRef.current = false;
        }, 0);
    }, [g, location.pathname, onPage, setLeadStatus, setLeadsDesc, setLeadsOrder, setShowSpinner, showToast, t]);

    useListRefreshEffect(listResetNonce, () => {
        void resetLeadsList();
    });

    useListRefreshEffect(listRefreshNonce, () => {
        prevListData.current = null;
        void fetchLeads(true);
    });

    useEffect(() => {
        if (page === 1) {
            fetchLeads(true);
        } else {
            setPage(1);
        }
    }, [g.search?.[location.pathname], g.target]);

    useEffect(() => {
        if (skipFetchOnStateChangeRef.current) return;
        fetchLeads();
    }, [page, leadsSatus, leadsOrder, leadsDesc, onPage]);

    useEffect(() => {
        const _page = g?.paginator?.[location.pathname]?.currentPage || 1;
        if (page !== _page) setPage(_page);
    }, [g?.paginator?.[location.pathname]?.currentPage, g?.paginator?.[location.pathname]?.countRows, g?.paginator?.[location.pathname]?.onPage]);

    return (
        <>
            <OffNewLead show={showNewLead} setShow={setShowNewLead} fetchLeads={fetchLeads} />

            <OffClientInfo clientId={clientInfoId} setClientId={setClientInfoId} />

            <OffTasks
                show={tasksLeadId !== null}
                onHide={() => setTasksLeadId(null)}
                leadId={tasksLeadId}
                contextName={
                    leadsList?.find((lead) => lead.ID === tasksLeadId)?.clientName ?? null
                }
            />

            <Row>
                <Col className="col-sticky" sm={12}>
                    <div className="w-100 d-sm-flex pt-1 justify-content-between align-items-start">
                        <div>
                            <h4 className="p-0 m-0 mb-0 me-2 text-break">{t('title')}</h4>
                            <TitleCounter />
                        </div>
                        <div className="mt-2 mt-sm-0">
                            <LeadToolBar setShowNewLead={setShowNewLead} />
                        </div>
                    </div>
                </Col>

                <Col sm={12}>
                     
                            {leadsList ? (
                                <LeadsTable
                                    leadsList={leadsList}
                                    fetchLeads={fetchLeads}
                                    setClientInfoId={setClientInfoId}
                                    setTasksLeadId={setTasksLeadId}
                                />
                            ) : (
                                <ESpinner />
                            )}
                        
                </Col>
            </Row>
        </>
    );
};

export default Leads;
